import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  Input,
  numberAttribute,
} from '@angular/core';

function numberOrZero(value: unknown): number {
  return numberAttribute(value, 0);
}

function numberOrOneHundred(value: unknown): number {
  return numberAttribute(value, 100);
}

@Component({
  selector: 'ds-progress-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="ds-progress-bar"
      [class.ds-progress-bar--auto]="autoPlay"
      [style.--ds-progress-bar-value]="progressPercent + '%'"
      [style.--ds-progress-bar-duration]="animationDurationMs + 'ms'"
      role="progressbar"
      [attr.aria-label]="ariaLabel"
      [attr.aria-valuemin]="min"
      [attr.aria-valuemax]="max"
      [attr.aria-valuenow]="autoPlay ? null : boundedValue"
    >
      <span class="ds-progress-bar__indicator"></span>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      min-width: var(--ds-progress-bar-min-width);
    }

    .ds-progress-bar {
      --ds-progress-bar-value: 0%;
      --ds-progress-bar-duration: 15000ms;
      position: relative;
      width: 100%;
      min-width: var(--ds-progress-bar-min-width);
      height: var(--ds-progress-bar-height);
      overflow: hidden;
      border-radius: var(--ds-progress-bar-border-radius);
      background: var(--ds-progress-bar-track-color);
    }

    .ds-progress-bar__indicator {
      position: absolute;
      inset: 0 auto 0 0;
      width: var(--ds-progress-bar-value);
      min-width: 0;
      border-radius: inherit;
      background: linear-gradient(
        90deg,
        var(--ds-progress-bar-indicator-gradient-start-color) 15%,
        var(--ds-progress-bar-indicator-gradient-end-color) 100%
      );
    }

    .ds-progress-bar--auto .ds-progress-bar__indicator {
      animation: ds-progress-bar-fill var(--ds-progress-bar-duration) linear infinite;
    }

    @keyframes ds-progress-bar-fill {
      from { width: 0%; }
      to { width: 100%; }
    }
  `],
})
export class DsProgressBarComponent {
  @Input({ transform: numberOrZero }) value = 0;
  @Input({ transform: numberOrZero }) min = 0;
  @Input({ transform: numberOrOneHundred }) max = 100;
  @Input({ transform: booleanAttribute }) autoPlay = false;
  @Input({ transform: numberAttribute }) animationDurationMs = 15000;
  @Input() ariaLabel = 'Progreso';

  get boundedValue(): number {
    return Math.min(this.max, Math.max(this.min, this.value));
  }

  get progressPercent(): number {
    const span = this.max - this.min;
    if (span <= 0) return 0;
    return Math.min(100, Math.max(0, ((this.value - this.min) / span) * 100));
  }
}
